import { useEffect, useRef, useState } from 'react';
import { Alert, Animated } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { StackActions, useNavigation } from '@react-navigation/native';
import { CANCEL_ERROR } from 'apisauce';
import dayjs from 'dayjs';
import {
  filter as lodashFilter,
  find as lodashFind,
  get as lodashGet,
  isEmpty as lodashIsEmpty,
  isNil as lodashIsNil,
  isUndefined as lodashIsUndefined,
  omitBy as lodashOmitBy,
  pick as lodashPick,
  sortBy as lodashSortBy,
} from 'lodash';

import modals from '../../../Components/Sheets/modals';
import constants from '../../../Config/constants';
import messages from '../../../Config/messages';
import AnalyticsHelper from '../../../Helper/Analytics';
import CheckoutHelper from '../../../Helper/Checkout';
import MenuItemHelper from '../../../Helper/MenuItem';
import Sentry from '../../../Helper/Sentry';
import urlParameterHelper from '../../../Helper/UrlParameter';
import useAlert from '../../../Hooks/useAlert';
import useCancellableRequest from '../../../Hooks/useCancellableRequest';
import useMealPlan from '../../../Hooks/useMealPlan';
import useStorePersist from '../../../Hooks/useStorePersist';
import routeList from '../../../Routes/list';
import { updateCheckoutDetails } from '../../../RTK/checkout';
import { storeCheckoutSelector } from '../../../RTK/checkout/selectors';
import { checkout } from '../../../RTK/defaultValues';
import {
  tagsFilterSelector,
  whenFilterSelector,
} from '../../../RTK/filter/selectors';
import { resetMealPlan } from '../../../RTK/mealPlan';
import {
  selectHasMealPlanItem,
  selectMealPlanCheckoutData,
} from '../../../RTK/mealPlan/selectors';
import {
  removeShopData,
  setRefetchMenu,
  setShopData,
  setShopRawData,
} from '../../../RTK/shop';
import {
  selectRefetchMenu,
  shopLoadingSelector,
  shopRawSelector,
  shopSelector,
} from '../../../RTK/shop/selectors';
import Service from '../../../Service';
import storeApi from '../../../Service/api/store';

const defaultState = { scroll: new Animated.Value(0) };

// this is for functional component
function useController(props) {
  const storeId = lodashGet(props, 'route.params.id');
  const orderDateParams = lodashGet(props, 'route.params.order_date');
  const orderTimeParams = lodashGet(props, 'route.params.order_time');
  const previewTokenParams = lodashGet(props, 'route.params.preview_token'); // if the store is view from brand settings preview site
  const isOrderForLater = lodashGet(props, 'route.params.orderForLater');
  const storeParamsData = lodashPick(props?.route?.params, [
    'distance',
    'duration',
  ]);

  const alert = useAlert();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const elementHeightRef = useRef({ header: 0, categories: 0 });
  const isMealPlanCheckingOutRef = useRef(false);
  const { cancelPendingRequest, createRequest } = useCancellableRequest();
  const { getStoreInfo, getStoreMenu } = useStorePersist({
    ...storeParamsData,
    orderDateParams,
    orderTimeParams,
    previewToken: previewTokenParams,
  });
  const { isMealPlan, isMealPlanCheckingOut, mealPlanType } = useMealPlan();

  const isMounted = useRef();
  const orderTypeHasBeenSet = useRef();
  const prevDateFilter = useRef({});

  const homeTagsFilter = useSelector(tagsFilterSelector);
  const searchTagsFilter = useSelector((state) => state.filter.search.tags);

  const hasMealPlanItem = useSelector(selectHasMealPlanItem);

  const whenFilter = useSelector(whenFilterSelector);
  const shopLoading = useSelector(shopLoadingSelector);
  const shopData = useSelector(shopSelector);
  const shopRawData = useSelector(shopRawSelector);
  const shouldRefetchMenu = useSelector(selectRefetchMenu);
  const checkoutData = useSelector((state) =>
    isMealPlan
      ? selectMealPlanCheckoutData(state)
      : storeCheckoutSelector(state, shopData?.id),
  );

  const [scroll] = useState(defaultState.scroll);
  const [isGettingMenu, setGettingMenu] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [menuError, setMenuError] = useState('');

  const scrollOffset =
    elementHeightRef.current.header + elementHeightRef.current.categories + 8; // section separator height is 8px

  const continueToPaymentData = checkoutData[checkout.keys.CONTINUE_TO_PAYMENT];
  const canContinueToPayment = !lodashIsEmpty(continueToPaymentData);

  // call _clearContinueToPaymentData every time checkout data change but dont call onload
  useEffect(() => {
    if (isMounted.current && canContinueToPayment) {
      _clearContinueToPaymentData(shopData?.id);
    }
  }, [
    checkoutData[checkout.keys.DELIVERY_ADDRESS],
    checkoutData[checkout.keys.DELIVERY_SCHEDULE],
    checkoutData[checkout.keys.ORDER_TYPE],
  ]);

  // should only execute once after shopData has been set
  // effect for setting the default order type in store page
  useEffect(() => {
    if (!lodashIsEmpty(shopData) && !orderTypeHasBeenSet.current) {
      // check for previous screen only for search
      // because aside from search, other screen to access store page must use the homepage tag filter
      const routes = navigation.getState().routes; // get routes
      const prevRoutes = routes[routes.length - 2]; // get previous routes
      const isPreviousRouteSearch =
        prevRoutes?.name === routeList.SEARCH ? true : false; // is previous page is search ?
      const homeOrderTypeFilter = lodashFind(homeTagsFilter, {
        paramName: 'order_type',
      }); // get tag filter on homepage
      const searchOrderTypeFilter = lodashFind(searchTagsFilter, {
        paramName: 'order_type',
      }); // get tag filter on search page
      const tagFilter = isPreviousRouteSearch
        ? searchOrderTypeFilter
        : homeOrderTypeFilter; // if previous route is search: use the search tag filter, else home tag filter
      if (!lodashIsEmpty(tagFilter)) {
        _changeOrderTypeToSelectedTagFilter(tagFilter);
      }
      orderTypeHasBeenSet.current = true; // for flag, for this not to be called every change of menu (meal plan to non meal plan vice versa)
    }
  }, [shopData]);

  // this effect is for handling toggle of menu list to meal plan and non meal plan
  useEffect(() => {
    if (!lodashIsEmpty(shopRawData?.menu)) {
      _setShopMenu(shopRawData); // toggle menu to meal plan or not meal plan
    }
  }, [
    // this effect should be called on load and ...
    shopRawData?.menu, // when menu list is changed (on fetched)
    isMealPlan, // if meal plan or not meal plan
    mealPlanType, // when selected meal plan type, just in case its not yet flipped
  ]);

  // this effect only handle fetching store data on load
  useEffect(() => {
    // added delay 100ms if from order for later section to propagate the changes on navigation route params
    setTimeout(_getStoreData, isOrderForLater ? 100 : 0);
  }, [storeId]); // listen for changes of storeId on url parameter

  useEffect(() => {
    const isNotSameDate = prevDateFilter.current.date !== whenFilter?.date;
    const isNotSameTime = prevDateFilter.current.time !== whenFilter?.time;
    if (
      isMounted.current && // must be mounted first
      // date and time filter changed
      (isNotSameDate || isNotSameTime || shouldRefetchMenu) // manually dispatched refetchMenu on shop redux state (e.g: when cart item become unavailable while checking out on branded)
    ) {
      // its either
      if (shouldRefetchMenu) {
        // manually refetch via redux state
        dispatch(setRefetchMenu(false));
      } else {
        // or date filter is changed
        prevDateFilter.current.date = whenFilter?.date || orderDateParams;
        prevDateFilter.current.time = whenFilter?.time || orderTimeParams;
        _clearContinueToPaymentData(shopData?.id); // clear continue to payment if date filter is changed
      }
      _getStoreMenu(shopData);
    }
  }, [shouldRefetchMenu, whenFilter?.date, whenFilter?.time]);

  // this handle making sure the flag on clean up works as intended not to clear when navigating to checkout page
  useEffect(() => {
    isMealPlanCheckingOutRef.current = isMealPlanCheckingOut;
  }, [isMealPlanCheckingOut]);

  // this effect only handle isMounted and clean up of store info and meal plan journey on unmount conditionally
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (!isMealPlanCheckingOutRef.current) {
        dispatch(resetMealPlan()); // clear meal plan journ
        dispatch(removeShopData()); // clear store data
      }
    };
  }, [dispatch]);

  const _changeOrderTypeToSelectedTagFilter = (orderTypeFilter) => {
    const orderType = orderTypeFilter.values[0].value;
    const type = lodashFind(constants.ORDER_TYPE_DATA, { value: orderType });
    dispatch(
      updateCheckoutDetails({
        store_id: shopData.id,
        keyToUpdate: checkout.keys.ORDER_TYPE,
        keyValue: type,
      }),
    );
  };

  // for resetting state value
  const _resetState = async (isRefreshing) => {
    scroll.setValue(0); // reset scroll animation value
    // reset states
    setGettingMenu(true);
    dispatch(setShopRawData());
    if (!lodashIsEmpty(shopData) && !isRefreshing) {
      // if shopData is not empty and not refreshing, it means store is already loaded and it was changed
      // due to change of storeId probably user click the store name in cart page while store page still loaded
      cancelPendingRequest(); // cancel all pending request
      dispatch(resetMealPlan()); // reset meal plan
      return new Promise((resolve) => setTimeout(resolve, 100)); // add little delay to propagate the changes on redux to other component
    }
  };

  // for getting store data
  const _getStoreData = async (isRefreshing) => {
    await _resetState(isRefreshing); // reset state to default
    dispatch(removeShopData()); // clear store/shop data

    if (storeId) {
      const { ok, data: response, problem } = await getStoreInfo(storeId);
      const isNotFound =
        response?.statusCode === 404 || response?.error === 'StoreNotFound';
      // stop the code from going if request is cancelled
      if (problem === CANCEL_ERROR) return;
      const data = lodashOmitBy(response, lodashIsNil);
      const okWithStoreInfo = Object.keys(data).length > 1;
      if (ok && okWithStoreInfo) {
        const temp = {
          ...data,
          category: [], // initial value for _getStoreMenu
          menu: [], // add initial value for _getStoreMenu
        };
        dispatch(setShopRawData(temp)); // save for filter of meal plan and not meal plan, must be save before calling _setShopMenu
        const isOk = await _setShopMenu(temp); // for showing initial UI of store page
        if (isOk) {
          _getStoreMenu(temp); // get store menu
        }
      } else if (constants.isWeb && !navigation.canGoBack() && isNotFound) {
        window.location.href = 'https://www.pickup.ph/not-found';
      } else {
        navigation.canGoBack() && navigation.goBack();
        const msg = data?.message || messages.COMMON_ERROR_MESSAGE;
        Sentry.reportError('Error getting store details', data);
        alert(
          !okWithStoreInfo ? 'Unavailable' : messages.COMMON_ERROR_TITLE,
          !okWithStoreInfo
            ? 'Store is unavailable.'
            : Service.handleErrorMessage(msg),
        );
      }
    } else {
      navigation.canGoBack() && navigation.goBack();
      Alert.alert('Please try again', 'Store ID is invalid.');
    }
  };

  // for getting store menu
  const _getStoreMenu = async (storeInfo) => {
    setGettingMenu(true);
    const { ok, data, problem } = await getStoreMenu(storeId);
    // stop the code from going if request is cancelled
    if (problem === CANCEL_ERROR) return;
    if (ok) {
      // filter category to show only visible category
      const visibleCategories = lodashFilter(data.categories, {
        visible: true,
      });
      // sort category
      const sortedVisibleCategories = lodashSortBy(visibleCategories, [
        'category_position',
      ]);
      const updatedMenu = {
        ...storeInfo,
        categories: sortedVisibleCategories,
        menu: data.menu,
        orderForLater: isOrderForLater,
      };

      // save store api raw data for storing to the store cache when user add to cart
      dispatch(setShopRawData(updatedMenu)); // save for filter of meal plan and not meal plan, must be save before calling _setShopMenu
      await _setShopMenu(updatedMenu);
    } else {
      const msg = data?.message || messages.COMMON_ERROR_MESSAGE;
      Sentry.reportError('Error getting store menu', data);
      setMenuError(msg); // show error message on the UI by updating its state
    }
    setGettingMenu(false); // mark getting store menu as done
    setRefreshing(false);
  };

  const _setShopMenu = async (data) => {
    const { menu, ...otherStoreData } = data;
    const constructedMenuList = [];

    const { isClosed, isOpenLater } = CheckoutHelper.isCanCheckout(
      otherStoreData.store_hours,
      otherStoreData.off_dates,
      otherStoreData.pre_order_to_order_queue_timer,
      whenFilter?.value,
    );

    // group the item to their category
    for (let i = 0; i < data?.categories?.length; i++) {
      const category = data.categories[i];
      if (
        isClosed ||
        isOpenLater ||
        category.stock === -1 ||
        category.stock > 0
      ) {
        // process only if category has stock
        let copyMenu = JSON.parse(JSON.stringify(menu)); // this to prevent warning, Attempted to assign to readonly property
        let categoryItems = lodashFilter(
          copyMenu,
          (e) => e.category === category.category_name,
        );
        const filteredCategoryItems = lodashFilter(categoryItems, (ci) => {
          ci.item.is_exclusive = category.is_exclusive; // add is_exclusive key to item
          ci.item.stock = ci.stock;
          ci.item.category_stock = category.stock;
          return MenuItemHelper.getAvailableItem(
            ci,
            isMealPlan,
            whenFilter?.date,
          );
        });
        // only push category if has category item
        if (!lodashIsEmpty(filteredCategoryItems)) {
          constructedMenuList.push({
            isExclusive: category.is_exclusive,
            title: category.category_name,
            subTitle: category.is_exclusive ? messages.MEAL_PLAN_EXCLUSIVE : '',
            categoryStock: MenuItemHelper.getCategoryOrItemStock(category), // for display purpose only of category stock
            data: lodashSortBy(filteredCategoryItems, ['order_position']), // sort item
          });
        }
      }
    }
    // #region modify constructedMenuList shape to only have flat item object instead of item.item
    // before { ...category_info, data: [{ item: { ...item_info }, ...other_info }] }
    constructedMenuList?.map?.((ml) => {
      ml.data = ml.data.map(({ item, ...rest }) => ({ ...item, ...rest }));
      return ml;
    });

    // after { ...category_info, data: [{ ...item_info, ...other_info }] }
    // #endregion
    if (isOrderForLater) {
      const now = dayjs();
      // if orderForLater is pass on params, store is access on order for later section
      // store the earliest time can pre order on the store.
      // order_for_later is to be access on useCart hooks on _addOrUpdateCart function
      // prettier-ignore

      const { ok, data } = await createRequest(
        storeApi.getStoreOrderDates,
        storeId
      );
      if (ok) {
        const results = lodashFind(data, {
          value: now.format(constants.DBDATE_FORMAT), // get current day (e.g: 0 - 6)
        });
        const timeOptions = results?.time_options?.filter(
          (item) => item.value !== 'asap_order_time',
        )[0];

        const availableFirstTimeSlot = {
          label: dayjs(timeOptions?.start).format(
            constants.DATE_DISPLAY_FORMAT,
          ),
          value: timeOptions?.start,
          date: results?.value,
          time: timeOptions?.value,
          isClosing: true,
          canAsap: false,
        };

        // set order_for_later date to use for payload of add/update cart date and time
        otherStoreData.order_for_later = availableFirstTimeSlot;

        if (lodashIsEmpty(otherStoreData.order_for_later)) {
          // if otherStoreData.order_for_later is empty it means store is not accepting advance order
          alert(
            otherStoreData?.name,
            'Sorry this store is not accepting advance order.',
          );

          return _goToHomePage();
        }
      } else {
        alert(otherStoreData?.name, 'Please try again.');
        return _goToHomePage();
      }
    }
    // save store information for store page to prevent props drilling
    dispatch(
      setShopData({
        ...otherStoreData,
        featured_items: constructedMenuList[0]?.data || [], // use first menu list data as featured items
        menu_list: constructedMenuList,
        is_preview: Boolean(previewTokenParams),
      }),
    );
    return true;
  };

  // when user click the floating text below store page (go to cart/basket page)
  const _goToCartPage = (storeId) => {
    navigation.navigate(routeList.BASKET, { activeTab: 0, storeId });
  };

  // when user click menu item
  const _onItemPressed = (item, context) => () => {
    AnalyticsHelper.itemSelection({
      ...item,
      fromSection: 'From store page menu',
    });
    modals.show(modals.PRODUCT, item, context);
  };

  const _goToHomePage = () => {
    if (constants.isWeb) {
      navigation.dispatch(
        StackActions.replace(routeList.HOME, {
          screen: 'MainMenu',
          params: { screen: routeList.ROOT_TAB },
        }),
      );
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const _onRefresh = () => {
    if (!shopLoading && !isGettingMenu) {
      setRefreshing(true);
      _getStoreData(true);
    }
  };

  const _showMealPlanList = () => modals.show(modals.MEAL_PLAN_LIST);

  // for showing the modal when user click the left menu of category (when user able to see the sticky category above)
  const _showMenuList = (reference) => async () => {
    const list = shopData.menu_list;
    const selectedIndex = await modals.show(modals.FULL_MENU_LIST, {
      list,
      value: reference.current?._reactInternals?.memoizedState?.currentIndex,
    });
    if (!lodashIsUndefined(selectedIndex)) {
      reference.current?.scrollToLocation?.(selectedIndex);
    }
  };

  const _clearContinueToPaymentData = (id) => {
    dispatch(
      updateCheckoutDetails({
        store_id: id,
        keyToUpdate: checkout.keys.CONTINUE_TO_PAYMENT,
        keyValue: undefined,
      }),
    );
  };

  return {
    elementHeightRef,
    hasMealPlanItem,
    isMealPlan,
    scrollOffset,
    state: { isGettingMenu, menuError, refreshing, scroll },
    goToCartPage: _goToCartPage,
    onItemPressed: _onItemPressed,
    onRefresh: _onRefresh,
    showMealPlanList: _showMealPlanList,
    showMenuList: _showMenuList,
  };
}

export default useController;
